import React, { useEffect, useRef, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { ChevronLeft } from "lucide-react";
import Loader from "../../../components/Loader/Loader";
import SLACountdown from "../shared/SLACountdown";
import DecisionForm from "../../../components/dr/DecisionForm";
import CredentialExpiryAlert from "../../../components/dr/CredentialExpiryAlert";
import PageShell from "../../../components/layout/PageShell";
import PageHead from "../../../components/layout/PageHead";
import Alert from "../../../components/v3/Alert";
import Btn from "../../../components/v3/Btn";
import ApprovalContextCards, { investorName, personName, KeyVal } from "./ApprovalContextCards";
import OversightActions from "./OversightActions";
import SuitabilityAnswersCard, { answersMatchItem } from "../../../components/dr/SuitabilityAnswersCard";
import { drApi } from "../../../services/drApi";
import { useRole } from "../../../hooks/useRole";
import useSuitabilityAnswers from "../../../hooks/useSuitabilityAnswers";
import { needsReviewedRevision } from "../../../utils/suitabilityRevision";
import {
  apiErrorCode,
  apiErrorMessage,
  isSubmissionSuperseded,
  submissionSupersededMessage,
} from "../../../utils/apiError";
import { sendNamingAccountOnce } from "../../../utils/decisionAccount";

const STATUS_CLASS = {
  PENDING:   "s-rev",
  COMPLETED: "s-active",
  BREACHED:  "s-fail",
  CANCELLED: "s-mute",
};

const AD_CANNOT_DECIDE =
  "Platform Manager sessions can reassign or escalate but do not decide KYC, classification or suitability.";
const CREDENTIAL_BLOCKED =
  "Your reviewer credential is missing or expired. Renew it on the Credentials page before deciding.";
const ALREADY_DECIDED = "Another reviewer has already decided this record.";
// The 409s of the decide that decided NOTHING (person-first WP6a fix round 1,
// F2). The write-through throws each before any write and the whole decision
// rolls back, so the item is still open and nobody else decided it. The
// decide repeats the code at the top level ({ code }; api dr.controller).
const NOTHING_DECIDED = Object.freeze({
  // kycStatus.personKycRequired: a company (KYB) approval for a person not verified.
  PERSON_KYC_REQUIRED:
    "Verify this person's identity before approving their company. Nothing was decided.",
  // kycStatus.needsInfoOnVerifiedCompany.
  needs_info_on_verified_company:
    "This company is verified, so asking for more information would leave it verified and locked, and the investor could neither correct nor resubmit it. To pull a verified company back, record Reject with a message for the investor. Nothing was decided.",
  // kycStatus.needsInfoOnVerifiedPerson (api WP4b): the person-side mirror.
  needs_info_on_verified_person:
    "This person is verified, so asking for more information would leave them verified. To pull a verified person back, record Reject with a message for the investor. Nothing was decided.",
  // complianceDecision.kindUnknownError: a pre-split item whose side is not on record.
  decision_kind_unknown:
    "This decision was recorded before person and company reviews were separated, and its side is not on record. Record a new person or company decision instead. Nothing was decided.",
});
// The account refusals of the decide (person-first WP6c; api WP5
// complianceDecision.decisionAccountOf). Each is thrown before any write and
// the decision rolls back, so nothing was decided, whatever the status: two
// are 409s, and neither is another reviewer's decision. The decide sends the
// code alone ({ message: code, code }), so the copy is the page's own.
const ACCOUNT_REFUSED = Object.freeze({
  ACCOUNT_KIND_REQUIRED:
    "This investor has a personal and a company account, and the server could not tell which one this decision is for. Nothing was decided.",
  ACCOUNT_SELECTOR_CONFLICT:
    "The account chosen is not the account this item is about. Nothing was decided.",
  ACCOUNT_NOT_SELECTABLE:
    "The investor account this item is about is closed, so it cannot be decided. Nothing was decided.",
  CORPORATE_ACCOUNT_NOT_SET_UP:
    "This investor has not set up a company account, so there is no company to decide for. Nothing was decided.",
  INVESTOR_ACCOUNT_NOT_FOUND: "The investor account was not found. Nothing was decided.",
});
const ACCOUNT_NOT_CHOSEN = "Nothing was decided. Choose Personal or Company to record this decision.";

function DRApprovalDetail() {
  const { slaId } = useParams();
  const navigate = useNavigate();
  // Deciding is a role, not a rank: DR, CCO and UDP all decide, and a Platform
  // Manager holding none of them oversees instead. The old branch keyed on AD,
  // which put a CCO who also holds AD on the wrong side of every gate here.
  // Two independent questions. canDecide chooses the live form; isAD decides
  // whether the oversight actions exist at all. They are not opposites: an
  // AD who also holds CCO both decides AND hands work on, and hanging
  // OversightActions off the else-branch of canDecide hid reassignment from
  // exactly the people most likely to need it.
  const { canDecide, isAD } = useRole();
  const queueRoute = canDecide ? "/dr/approvals" : "/ad/dr-oversight/sla-breaches";
  const [loading, setLoading] = useState(true);
  const [payload, setPayload] = useState(null);
  const [error, setError] = useState(null);
  const [flash, setFlash] = useState(null);
  const [investorMessage, setInvestorMessage] = useState("");

  // API round 15 (Diego, 28 Oct): a SUITABILITY item about an order is decided
  // on the answers it is about. They are read and shown here, and a decision
  // is offered only while the answers on screen belong to the item's
  // submission; it carries the revision shown (reviewed_submission_rev).
  //
  // Codex r14 P1: the page belongs to the item the route names. It is
  // remounted per slaId (DRApprovalDetailRoute below), its own refetches are
  // sequenced (only the newest lands), and a decision is offered only for a
  // payload whose id is the route's, with answers read for that item's own
  // investment (answersMatchItem): two orders can share a revision number.
  const slaRow = payload?.sla;
  const routeItem = !!slaRow && String(slaRow.id) === String(slaId);
  const bindsAnswers = routeItem && needsReviewedRevision(slaRow);
  const answers = useSuitabilityAnswers(bindsAnswers ? slaRow.investment_id : null);
  const decisionBound = routeItem && (!bindsAnswers || answersMatchItem(answers, slaRow));
  const detailSeq = useRef(0);

  useEffect(() => {
    fetchDetail();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [slaId]);

  async function fetchDetail() {
    const mine = ++detailSeq.current;
    setLoading(true);
    try {
      const res = await drApi.approvalDetail(slaId);
      if (mine !== detailSeq.current) return;
      setPayload(res?.data || null);
      setError(null);
    } catch (err) {
      if (mine !== detailSeq.current) return;
      setError(apiErrorMessage(err, "Fetch failed"));
    } finally {
      if (mine === detailSeq.current) setLoading(false);
    }
  }

  async function submitDecision({ decision, notes }) {
    if (!decisionBound) throw new Error("The answers on this page do not belong to this review item. Reload the page.");
    try {
      const body = { decision, notes };
      const message = investorMessage.trim();
      if (message) body.investor_message = message;
      if (bindsAnswers) body.reviewed_submission_rev = answers.submissionRev;
      // Person-first WP6c: the body names no account. Only when the server
      // answers 400 ACCOUNT_KIND_REQUIRED (a legacy CLASSIFICATION item, two
      // accounts) is the reviewer asked Personal or Company, and the same body
      // sent once more with account_kind. A cancel sends nothing more.
      const sent = await sendNamingAccountOnce((b) => drApi.decide(slaId, b), body);
      if (sent.cancelled) throw new Error(ACCOUNT_NOT_CHOSEN);
      const res = sent.response;
      // Honest copy: the investor notification is enqueued (cron-delivered),
      // not sent synchronously. Delivery is confirmable on the email log.
      setFlash({
        tone: "info",
        text: `Decision recorded, ${res?.data?.decision || decision}. Investor notification queued. Returning to queue...`,
      });
      setInvestorMessage("");
      setTimeout(() => navigate(queueRoute), 1200);
    } catch (err) {
      const status = err?.response?.status;
      const code = apiErrorCode(err);
      // An account refusal wrote nothing, at any status: its own copy, inline,
      // no refetch, and the notes stay.
      const accountRefused = err?.response ? ACCOUNT_REFUSED[code] : null;
      if (accountRefused) throw new Error(accountRefused);
      // A refusal that wrote nothing is not a race: it gets its own copy,
      // inline next to the form, and no refetch, so the notes stay.
      const nothingDecided = status === 409 ? NOTHING_DECIDED[code] : null;
      if (nothingDecided) {
        throw new Error(nothingDecided);
      }
      if (status === 409) {
        // The losing half of a race, not a failure: say so and refetch so the
        // decision that won is on screen. A resubmission is a different race:
        // the investor replaced the answers, the item closed without touching
        // the order, and nobody else decided anything.
        setFlash({
          tone: "danger",
          text: isSubmissionSuperseded(err) ? submissionSupersededMessage(err) : ALREADY_DECIDED,
        });
        await fetchDetail();
        // The answers may have moved on with the submission: read them again.
        if (bindsAnswers) answers.reload();
        return;
      }
      // Rethrown rather than flashed: DecisionForm renders a submit failure
      // inline, next to the control that produced it.
      if (status === 403 && code === "ad_cannot_decide") {
        throw new Error(AD_CANNOT_DECIDE);
      }
      if (status === 403 && code === "dr_credential_expired_or_missing") {
        throw new Error(CREDENTIAL_BLOCKED);
      }
      throw err;
    }
  }

  // The oversight actions unmount themselves by refetching, so the page owns
  // the confirmation they produce.
  async function handleOversightDone(message) {
    if (message) setFlash({ tone: "info", text: message });
    await fetchDetail();
  }

  const sla = payload?.sla;
  const investor = payload?.investor;
  // The detail payload has no `decided_by_user_id` field at all: it publishes
  // the DecidedBy association and the decision instead. Keying only on the id
  // meant a decided item still rendered a live decision form.
  const isDecided =
    sla?.status === "COMPLETED" ||
    !!sla?.decided_by_user_id ||
    !!sla?.DecidedBy ||
    !!sla?.decided_at;

  const decidedByName = personName(
    sla?.DecidedBy || sla?.decided_by,
    sla?.decided_by_user_id
  );

  const headActions = (
    <Btn variant="ghost" size="sm" onClick={() => navigate(queueRoute)}>
      <ChevronLeft /> Back to {canDecide ? "queue" : "SLA breaches"}
    </Btn>
  );

  return (
    <PageShell>
      <Loader isLoading={loading} />
      <PageHead
        title={sla ? `${sla.submission_type} · SLA #${sla.id}` : "Approval detail"}
        subtitle={sla && investor ? <span>{investorName(investor)}</span> : null}
        actions={headActions}
      />

      {canDecide && <CredentialExpiryAlert />}

      {error && <Alert tone="danger">{error}</Alert>}
      {flash && <Alert tone={flash.tone}>{flash.text}</Alert>}

      {!loading && sla && (
        <>
          <section className="card">
            <div className="card__head">
              <div>
                <div className="card__title">{investorName(investor)}</div>
                <div className="card__sub mono">
                  Submitted {new Date(sla.submitted_at).toLocaleString()}
                </div>
              </div>
              <div className="card__head-right">
                <SLACountdown deadline={sla.deadline_at} size="large" />
                <span className={`tbl-status ${STATUS_CLASS[String(sla.status).toUpperCase()] || "s-mute"}`}>
                  {sla.status}{sla.decision ? ` · ${sla.decision}` : ""}
                </span>
              </div>
            </div>
          </section>

          <ApprovalContextCards payload={payload} />

          {bindsAnswers && (
            <SuitabilityAnswersCard
              answers={answers}
              item={slaRow}
              onOpenSuitabilityPage={() => navigate(`/suitability-overview/${slaRow.investment_id}`)}
            />
          )}

          <section className="card">
            <div className="card__head"><div className="card__title">Decision</div></div>
            <div className="card__body">
              {isDecided ? (
                <>
                  <KeyVal label="Decision" value={sla.decision || "-"} />
                  <KeyVal label="Decided by" value={decidedByName} />
                  <KeyVal
                    label="Decided at"
                    value={sla.decided_at ? new Date(sla.decided_at).toLocaleString() : "-"}
                  />
                  <KeyVal label="Review status" value={sla.review_status || "Not reviewed"} />
                </>
              ) : canDecide ? (
                <>
                  <DecisionForm
                    notesLabel="Decision notes (regulatory audit trail)"
                    submitLabel="Record decision"
                    disabled={!decisionBound}
                    onSubmit={submitDecision}
                  />
                  <div className="field" style={{ marginTop: 12 }}>
                    <label className="field__label" htmlFor="dr-investor-message">
                      Message to the investor (optional)
                    </label>
                    <textarea
                      id="dr-investor-message"
                      className="field__textarea"
                      rows={3}
                      value={investorMessage}
                      onChange={(e) => setInvestorMessage(e.target.value)}
                      placeholder="Included in the notification sent to the investor."
                    />
                  </div>
                </>
              ) : (
                <DecisionForm disabled onSubmit={submitDecision} />
              )}
            </div>
          </section>

          {isAD && !isDecided && (
            <section className="card">
              <div className="card__head">
                <div className="card__title">Platform Manager actions</div>
              </div>
              <div className="card__body">
                <OversightActions slaId={slaId} onDone={handleOversightDone} />
              </div>
            </section>
          )}
        </>
      )}
    </PageShell>
  );
}

/**
 * Remounted per item, as InvestmentEdit is per order (InvestmentEditRoute):
 * a new slaId starts a new page, so no response, timer or callback of the
 * previous item can reach it (Codex r14 P1).
 */
function DRApprovalDetailRoute() {
  const { slaId } = useParams();
  return <DRApprovalDetail key={slaId} />;
}

export default DRApprovalDetailRoute;
